import { formatKes } from "lib/format";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { createColumnHelper } from "@tanstack/react-table";
import Card from "components/card";
import ExpandableDataTable from "components/admin/ExpandableDataTable";
import { RevenueStatementView } from "components/revenue/RevenueStatement";
import { apiGet, apiPost, buildQueryString } from "lib/apiClient";
import { useAuth } from "contexts/AuthContext";
import {
  settlementPeriod,
  settlementExportColumns,
  renderSettlementDetail,
  SettlementStatusBadge,
  MoneyTimelineLink,
  viewButtonClass,
} from "views/portal/settlements";

const columnHelper = createColumnHelper();
const PAGE_SIZE = 20;

function pct(value) {
  const n = Number(value);
  return Number.isFinite(n) ? `${n.toLocaleString("en-KE", { maximumFractionDigits: 3 })}%` : `${value}%`;
}

const SOURCE_META = {
  DEPOSIT_FEE: { label: "Deposit", tone: "blue" },
  DEPOSIT_FEE_FIXED: { label: "Deposit · flat fee", tone: "blue" },
  WITHDRAWAL_FEE: { label: "Withdrawal", tone: "amber" },
  TRADING_FEE: { label: "Trading", tone: "green" },
  TRADING_COMMISSION: { label: "Commission on profits", tone: "green" },
  MARKET_SETTLEMENT_SPREAD: { label: "Market settlement", tone: "gray" },
};

function WalletCard() {
  const { hasPermission } = useAuth();
  const canWithdraw = hasPermission("client-portal.manage");

  const [wallet, setWallet] = useState(null);
  const [withdrawals, setWithdrawals] = useState([]);
  const [payoutAccount, setPayoutAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [formOpen, setFormOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [walletResult, withdrawalsResult, payoutAccountResult] = await Promise.all([
        apiGet("/portal/wallet"),
        apiGet(`/portal/wallet/withdrawals${buildQueryString({ page: 1, pageSize: 10 })}`),
        apiGet("/portal/payout-account").catch(() => null),
      ]);
      setWallet(walletResult.wallet);
      setWithdrawals(withdrawalsResult.withdrawals || []);
      setPayoutAccount(payoutAccountResult);
    } catch (err) {
      setError(err.message || "Failed to load your wallet.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => {
    if (!formOpen || !amount || Number(amount) <= 0) {
      setPreview(null);
      return undefined;
    }
    let cancelled = false;
    setPreviewLoading(true);
    apiPost("/portal/wallet/withdrawals/preview", { amount: Number(amount).toFixed(4) })
      .then((result) => {
        if (!cancelled) setPreview(result);
      })
      .catch(() => {
        if (!cancelled) setPreview(null);
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [formOpen, amount]);

  async function submitWithdrawal() {
    if (!payoutAccount?.externalAccount?.id) {
      setSubmitError("Set up a payout account first, on the Payout Account page.");
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      await apiPost("/portal/wallet/withdrawals", {
        amount: Number(amount).toFixed(4),
        currency: wallet?.currency || "KES",
        externalAccountId: payoutAccount.externalAccount.id,
        idempotencyKey: `portal-withdraw-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      });
      setFormOpen(false);
      setAmount("");
      await load();
    } catch (err) {
      setSubmitError(err.message || "Failed to submit the withdrawal.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Card extra="p-5 mb-5">
      <p className="text-lg font-semibold text-navy-700 dark:text-white">Revenue Share wallet</p>
      <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
        Real, ledger-backed money owed to your organization, credited the instant a revenue event is finalized and
        its share calculated, not merely an analytics figure.
      </p>

      {error && <p className="mt-3 text-sm text-red-500">{error}</p>}

      {wallet && (
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div className="rounded-xl border border-gray-200 p-4 dark:border-white/10">
            <p className="text-xs font-medium text-muted-foreground">Total earned</p>
            <p className="mt-1 text-xl font-semibold text-navy-700 dark:text-white">{formatKes(wallet.totalEarned, wallet.currency)}</p>
          </div>
          <div className="rounded-xl border border-gray-200 p-4 dark:border-white/10">
            <p className="text-xs font-medium text-muted-foreground">Available to withdraw</p>
            <p className="mt-1 text-xl font-semibold text-green-500">{formatKes(wallet.availableBalance, wallet.currency)}</p>
          </div>
          <div className="rounded-xl border border-gray-200 p-4 dark:border-white/10">
            <p className="text-xs font-medium text-muted-foreground">Pending</p>
            <p className="mt-1 text-xl font-semibold text-navy-700 dark:text-white">{formatKes(wallet.reservedBalance, wallet.currency)}</p>
          </div>
          <div className="rounded-xl border border-gray-200 p-4 dark:border-white/10">
            <p className="text-xs font-medium text-muted-foreground">Withdrawn</p>
            <p className="mt-1 text-xl font-semibold text-navy-700 dark:text-white">{formatKes(wallet.withdrawnTotal, wallet.currency)}</p>
          </div>
        </div>
      )}

      {canWithdraw && (
        <div className="mt-4">
          {!formOpen ? (
            <button
              type="button"
              onClick={() => setFormOpen(true)}
              disabled={!wallet || Number(wallet.availableBalance) <= 0}
              className="h-10 rounded-sm bg-primary px-4 text-sm font-semibold text-primary-foreground disabled:opacity-50"
            >
              Withdraw
            </button>
          ) : (
            <div className="rounded-xl border border-gray-200 p-4 dark:border-white/10">
              <label className="text-xs font-medium text-muted-foreground">
                Amount (KES)
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="mt-1 h-10 w-full rounded-sm border border-gray-200 px-3 text-sm dark:border-white/10 dark:bg-navy-900 dark:text-white"
                />
              </label>

              {previewLoading && <p className="mt-2 text-xs text-gray-500">Calculating…</p>}
              {preview && !previewLoading && (
                <div className="mt-3 rounded-lg bg-gray-50 p-3 text-sm dark:bg-navy-900">
                  <div className="flex justify-between"><span className="text-gray-500">Requested</span><span>{formatKes(preview.requestedAmount, preview.currency)}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Platform fees charged</span><span>{formatKes(preview.withdrawalFee, preview.currency)}</span></div>
                  <div className="flex justify-between font-semibold"><span>You receive</span><span>{formatKes(preview.netPayout, preview.currency)}</span></div>
                  <p className="mt-1 text-xs text-gray-500">This is the amount that reaches your payout account.</p>
                </div>
              )}

              {!payoutAccount?.externalAccount && (
                <p className="mt-3 text-sm text-amber-600">
                  No payout account on file. <Link to="/portal/payout-account" className="font-semibold underline">Set one up</Link> before withdrawing.
                </p>
              )}
              {payoutAccount?.externalAccount && (
                <p className="mt-3 text-xs text-gray-500">Pays out to {payoutAccount.externalAccount.maskedLabel}.</p>
              )}

              {submitError && <p className="mt-2 text-sm text-red-500">{submitError}</p>}

              <div className="mt-4 flex gap-2">
                <button
                  type="button"
                  onClick={submitWithdrawal}
                  disabled={submitting || !amount || Number(amount) <= 0 || !payoutAccount?.externalAccount}
                  className="h-9 rounded-sm bg-primary px-4 text-sm font-semibold text-primary-foreground disabled:opacity-50"
                >
                  {submitting ? "Submitting…" : "Confirm withdrawal"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setFormOpen(false);
                    setAmount("");
                    setSubmitError(null);
                  }}
                  className="h-9 rounded-sm border border-gray-200 px-4 text-sm font-semibold text-navy-700 dark:border-white/10 dark:text-white"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {withdrawals.length > 0 && (
        <div className="mt-5">
          <p className="mb-2 text-sm font-semibold text-navy-700 dark:text-white">Recent withdrawals</p>
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="text-xs text-gray-500">
                <th className="py-1.5">Date</th>
                <th className="py-1.5">Amount</th>
                <th className="py-1.5">Platform fees charged</th>
                <th className="py-1.5">You receive</th>
                <th className="py-1.5">Status</th>
              </tr>
            </thead>
            <tbody>
              {withdrawals.map((w) => (
                <tr key={w.id} className="border-t border-gray-100 dark:border-navy-700">
                  <td className="py-1.5">{new Date(w.createdAt).toLocaleString()}</td>
                  <td className="py-1.5">{formatKes(w.amount, w.currency)}</td>
                  <td className="py-1.5">{formatKes(w.feeAmount, w.currency)}</td>
                  <td className="py-1.5">{formatKes(w.netPayoutAmount, w.currency)}</td>
                  <td className="py-1.5">{w.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {loading && !wallet && <p className="mt-3 text-sm text-gray-500">Loading your wallet…</p>}
    </Card>
  );
}

export default function RevenuePage() {
  const [period, setPeriod] = useState("30d");
  const [page, setPage] = useState(1);
  const [rules, setRules] = useState([]);
  const [settlements, setSettlements] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);

  const load = useCallback(
    async (background = false) => {
      if (!background) setLoading(true);
      setError(null);
      try {
        const query = buildQueryString({ page, pageSize: PAGE_SIZE });
        const result = await apiGet(`/portal/revenue${query}`);
        setRules(result.rules || []);
        setSettlements(result.settlements || []);
        setTotal(result.total || 0);
      } catch (err) {
        if (!background) setError(err.message || "Failed to load revenue data.");
      } finally {
        if (!background) setLoading(false);
      }
    },
    [page, reloadToken],
  );

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (page !== 1) return undefined;
    const id = setInterval(() => load(true), 15000);
    return () => clearInterval(id);
  }, [page, load]);

  const columns = [
    columnHelper.accessor("periodStart", {
      header: "Period",
      cell: (info) => <p className="text-sm font-semibold text-navy-700 dark:text-white">{settlementPeriod(info.row.original)}</p>,
      meta: { wrap: true },
    }),
    columnHelper.accessor("grossRevenue", {
      header: "Gross",
      cell: (info) => <p className="text-sm text-navy-700 dark:text-white">{formatKes(info.getValue(), info.row.original.currency)}</p>,
      meta: { hideBelow: "lg" },
    }),
    columnHelper.accessor("netRevenue", {
      header: "Net",
      cell: (info) => <p className="text-sm text-navy-700 dark:text-white">{formatKes(info.getValue(), info.row.original.currency)}</p>,
      meta: { hideBelow: "md" },
    }),
    columnHelper.accessor("clientSharePercentage", {
      header: "Share %",
      cell: (info) => <p className="text-sm text-navy-700 dark:text-white">{pct(info.getValue())}</p>,
      meta: { hideBelow: "lg" },
    }),
    columnHelper.accessor("clientShareAmount", {
      header: "Your revenue",
      cell: (info) => <p className="text-sm font-semibold text-navy-700 dark:text-white">{formatKes(info.getValue(), info.row.original.currency)}</p>,
    }),
    columnHelper.accessor("status", {
      header: "Status",
      cell: (info) => <SettlementStatusBadge status={info.getValue()} />,
    }),
  ];

  return (
    <div>
      <div className="mb-5">
        <RevenueStatementView basePath="/portal/revenue" period={period} onPeriodChange={setPeriod} />
      </div>

      <WalletCard />

      <Card extra="p-5 mb-5">
        <p className="text-lg font-semibold text-navy-700 dark:text-white">Revenue share rules</p>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
          The rules that determine how your organization's share of platform revenue is calculated.
        </p>
        {rules.length === 0 ? (
          <p className="mt-3 text-sm text-gray-500">No revenue share rules have been set up yet.</p>
        ) : (
          <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
            {rules.map((rule) => (
              <div key={rule.id} className="rounded-xl border border-gray-200 p-4 dark:border-white/10">
                <p className="text-sm font-semibold text-navy-700 dark:text-white">
                  {rule.marketId ? "Market-specific rule" : "Tenant-wide rule"} · {rule.basis.replaceAll("_", " ")}
                </p>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                  {rule.percentage ? `${rule.percentage}% share` : "No percentage set"}
                  {rule.fixedComponent ? ` + ${rule.fixedComponent} fixed` : ""}
                  {" of "}
                  {rule.sourceType ? (SOURCE_META[rule.sourceType]?.label ?? rule.sourceType).toLowerCase() + " revenue" : "all revenue"}
                </p>
                <p className="mt-1 text-xs text-gray-500">
                  {rule.settlementFrequency} · effective {new Date(rule.effectiveFrom).toLocaleDateString()}
                  {rule.effectiveTo ? ` – ${new Date(rule.effectiveTo).toLocaleDateString()}` : " onward"}
                </p>
              </div>
            ))}
          </div>
        )}
      </Card>

      <ExpandableDataTable
        title="Revenue by settlement period"
        subtitle="Expand a period for gross, deductions, net, your share percentage and the amount, with the formula spelled out."
        columns={columns}
        exportColumns={settlementExportColumns}
        data={settlements}
        loading={loading}
        error={error}
        onRetry={() => setReloadToken((t) => t + 1)}
        emptyMessage="No revenue has been calculated for your organization yet."
        page={page}
        pageSize={PAGE_SIZE}
        total={total}
        onPageChange={setPage}
        rowLabel={(s) => `period ${settlementPeriod(s)}`}
        headerActions={<MoneyTimelineLink />}
        renderDetail={renderSettlementDetail}
        renderActions={(s) => (
          <>
            <Link to="/portal/settlements" className={viewButtonClass()}>
              Open in settlements
            </Link>
            <Link to="/portal/money-timeline?type=CLIENT_SETTLEMENT" className={viewButtonClass()}>
              See it on the money timeline
            </Link>
          </>
        )}
      />
    </div>
  );
}
